import apiClient from './client';

export type DiscountAudience = 'public' | 'targeted';

export interface MembershipPlanDiscount {
  id: string;
  plan_id: string;
  term_months: number;
  label: string | null;
  audience: DiscountAudience;
  discount_type: 'percent_discount' | 'amount_discount' | 'free_months' | 'final_amount';
  percent_discount: string | null;
  amount_discount: string | null;
  free_months: number | null;
  final_amount: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/** A single per-member plan offer returned from the catalog endpoint. */
export interface MemberPlanOffer {
  plan_id: string;
  plan: MembershipPlan;
  /** Which term lengths are available for this offer. */
  allowed_term_months: number[];
  /** Pre-selected term (e.g. the targeted discount's specific term). */
  preselected_term_months: number | null;
  /** The targeted/dependent discount applied to this offer, if any. */
  discount: MembershipPlanDiscount | null;
  /** 'targeted' | 'dependent' | null for public discounts. */
  discount_source: string | null;
  /** Dependent-tier discount for this member, also present when a targeted offer wins. */
  tier_discount?: MembershipPlanDiscount | null;
  /** Every targeted offer this member holds on the plan (one per term). */
  targeted_discounts?: MembershipPlanDiscount[];
  /** If true this is an exclusive targeted offer (plan is hidden from general users). */
  is_exclusive: boolean;
}

/** Per-member entry in the plan catalog response. */
export interface MemberPlanCatalogEntry {
  user_id: string;
  offers: MemberPlanOffer[];
  /** Plan pre-selected for this member (e.g. exclusive targeted offer). */
  preselected_plan_id: string | null;
}

export interface MemberPlanCatalogResponse {
  entries: MemberPlanCatalogEntry[];
}

export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface MembershipPlan {
  id: string;
  machine_name: string;
  price: string;
  is_active: boolean;
  is_kids: boolean;
  is_archived: boolean;
  active_memberships_limit: number | null;
  sort_order: number;
  allowed_start_time?: string | null;
  allowed_end_time?: string | null;
  allowed_days_of_week?: DayOfWeek[] | null;
  member_count?: number;
  discounts: MembershipPlanDiscount[];
  created_at?: string;
  updated_at?: string;
}

export interface MembershipBillingDetails {
  term_months: number;
  line_amount: string;
  discount_id: string | null;
  discount_label: string | null;
}

export interface UserMembership {
  id: string;
  user_id: string;
  plan_id: string;
  start_date: string;
  end_date: string | null;
  status: string;
  plan: MembershipPlan | null;
  pending_plan_id: string | null;
  pending_plan: MembershipPlan | null;
  pending_cancel: boolean;
  renewal_term_months: number;
  pending_renewal_term_months: number | null;
  billing_details?: MembershipBillingDetails | null;
}

export interface MembershipChangePlanResponse {
  kind: 'prorated_payment' | 'scheduled';
  membership: UserMembership | null;
  payment: BatchVietQRCreateResponse | null;
}

export interface BatchMembershipChangePlanRequest {
  changes: { membership_id: string; new_plan_id: string }[];
}

export interface BatchMembershipChangePlanResponse {
  payment: BatchVietQRCreateResponse | null;
  scheduled: UserMembership[];
}

export interface PaymentRecord {
  id: string;
  user_id: string;
  amount: string;
  currency: string;
  status: string;
  payment_method: string | null;
  order_number: string | null;
  onepay_transaction_id: string | null;
  onepay_response_code: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface CardToken {
  id: string;
  user_id: string;
  card_brand: string;
  last_four: string;
  is_active: boolean;
  label: string | null;
  created_at: string;
}

export interface OnePayInitiateResponse {
  payment_url: string;
  payment_record_id: string;
}

export interface ActiveMembershipMember {
  user_id: string;
  user_name: string;
  membership: UserMembership;
}

export interface ActiveMembershipResponse {
  has_active_membership: boolean;
  members: ActiveMembershipMember[];
}

export interface SubUser {
  id: string;
  first_name: string;
  last_name: string;
  dob: string | null;
  role: string;
  is_active: boolean;
  membership: UserMembership | null;
}

export interface OrderItem {
  label: string;
  description: string;
  subtotal: string;
  discount_amount: string;
  final_amount: string;
}

export interface PendingVerificationResponse {
  payment_id: string | null;
  order_number: string | null;
  amount: string | null;
  qr_url: string | null;
  bank_bin: string | null;
  account_number: string | null;
  account_name: string | null;
  user_marked_paid_at: string | null;
  order_items: OrderItem[] | null;
}

export interface BatchVietQRCreateResponse {
  payment_id: string;
  order_number: string;
  amount: string;
  qr_url: string;
  bank_bin: string;
  account_number: string;
  account_name: string;
  membership_ids: string[];
  order_items: OrderItem[];
  status: string;
}

export interface PendingMemberRequest {
  first_name: string;
  last_name: string;
  dob: string;
  plan_id: string;
  relationship_type?: string;
  term_months?: number;
  weight_kg?: number | null;
  height_cm?: number | null;
  measurement_unit?: 'metric' | 'imperial';
}

export interface FamilyMembership {
  user_id: string;
  user_name: string;
  dob: string | null;
  membership: UserMembership | null;
  weight_kg: number | null;
  height_cm: number | null;
  measurement_unit: 'metric' | 'imperial' | null;
  email?: string | null;
}

export const paymentsApi = {
  // Plans
  getPlans: () =>
    apiClient.get<MembershipPlan[]>('/payments/plans').then((r) => r.data),

  // Per-member context-aware plan catalog
  getPlanCatalog: (memberUserIds?: string[]) => {
    const params = memberUserIds?.length
      ? `?member_user_ids=${encodeURIComponent(memberUserIds.join(','))}`
      : '';
    return apiClient
      .get<MemberPlanCatalogResponse>(`/payments/plans/catalog${params}`)
      .then((r) => r.data);
  },

  // Memberships
  getMyMemberships: () =>
    apiClient.get<UserMembership[]>('/payments/memberships/me').then((r) => r.data),

  // Active membership check
  getActiveMembership: () =>
    apiClient.get<ActiveMembershipResponse>('/payments/memberships/active').then((r) => r.data),

  // Payment history
  getPaymentHistory: () =>
    apiClient.get<PaymentRecord[]>('/payments/history').then((r) => r.data),

  // Saved cards
  getSavedCards: () =>
    apiClient.get<CardToken[]>('/payments/cards').then((r) => r.data),

  deleteSavedCard: (tokenId: string) =>
    apiClient.delete(`/payments/cards/${tokenId}`),

  // OnePay: first-time card payment (redirect flow)
  initiatePayment: (data: {
    membership_id: string;
    return_url: string;
    save_card?: boolean;
    locale?: string;
    language?: string;
  }) =>
    apiClient
      .post<OnePayInitiateResponse>('/payments/onepay/initiate', data)
      .then((r) => r.data),

  // OnePay: pay with saved token (recurring)
  tokenPayment: (data: {
    membership_id: string;
    card_token_id: string;
    return_url: string;
    locale?: string;
    language?: string;
  }) =>
    apiClient
      .post<OnePayInitiateResponse>('/payments/onepay/token-pay', data)
      .then((r) => r.data),

  // VietQR: check payment status (for refresh recovery)
  getVietQRPaymentStatus: (paymentId: string) =>
    apiClient
      .get<{ status: string; payment_id: string; user_marked_paid_at: string | null }>(`/payments/vietqr/status/${paymentId}`)
      .then((r) => r.data),

  // VietQR: user marks they paid
  markVietQRPaid: (paymentId: string) =>
    apiClient
      .post<{ payment_id: string; user_marked_paid_at: string }>(`/payments/vietqr/${paymentId}/mark-paid`)
      .then((r) => r.data),

  // Get pending verification payment for banner tracking
  getPendingVerification: () =>
    apiClient
      .get<{
        payment_id: string | null;
        order_number: string | null;
        amount: string | null;
        qr_url: string | null;
        bank_bin: string | null;
        account_number: string | null;
        account_name: string | null;
        user_marked_paid_at: string | null;
        order_items: OrderItem[] | null;
      }>('/payments/pending-verification')
      .then((r) => r.data),

  // Cancel any pending payment (generic, any method)
  cancelPayment: (paymentId: string) =>
    apiClient
      .post<{ status: string; payment_id: string; membership_ids: string[] }>('/payments/cancel', { payment_id: paymentId })
      .then((r) => r.data),

  // Sub-users: create dependent without email/auth
  createSubUser: (data: {
    first_name: string;
    last_name: string;
    dob: string;
    relationship_type?: string;
  }) =>
    apiClient.post<SubUser>('/payments/sub-users', data).then((r) => r.data),

  // Sub-users: list dependents
  getSubUsers: () =>
    apiClient.get<SubUser[]>('/payments/sub-users').then((r) => r.data),

  // Generic: initiate a payment order (method is determined by the backend based on endpoint)
  initiateOrder: (data: {
    memberships?: { user_id: string; plan_id: string; term_months?: number }[];
    pending_members?: PendingMemberRequest[];
    language?: string;
    referral_code?: string | null;
  }) =>
    apiClient
      .post<BatchVietQRCreateResponse>('/payments/vietqr/batch-create', data)
      .then((r) => r.data),

  // Update an existing pending/cancelled VietQR order in-place (reuse payment_id + order_number)
  updateOrder: (data: {
    payment_id: string;
    memberships?: { user_id: string; plan_id: string; term_months?: number }[];
    pending_members?: PendingMemberRequest[];
    language?: string;
    referral_code?: string | null;
  }) =>
    apiClient
      .post<BatchVietQRCreateResponse>('/payments/vietqr/batch-update', data)
      .then((r) => r.data),

  // Membership plan change (prorated upgrade OR scheduled downgrade)
  changeMembershipPlan: (membershipId: string, newPlanId: string, language?: string) =>
    apiClient
      .post<MembershipChangePlanResponse>(
        `/payments/memberships/${membershipId}/change-plan`,
        { new_plan_id: newPlanId, language },
      )
      .then((r) => r.data),

  // Batch membership plan changes (one payment for all upgrades)
  batchChangeMembershipPlans: (data: BatchMembershipChangePlanRequest & { language?: string }) =>
    apiClient
      .post<BatchMembershipChangePlanResponse>(
        '/payments/memberships/batch-change-plan',
        data,
      )
      .then((r) => r.data),

  // Schedule cancellation of a membership at end of term
  scheduleMembershipCancel: (membershipId: string) =>
    apiClient
      .post<UserMembership>(`/payments/memberships/${membershipId}/schedule-cancel`)
      .then((r) => r.data),

  // Clear any pending plan change / scheduled cancel
  clearScheduledChange: (membershipId: string) =>
    apiClient
      .post<UserMembership>(`/payments/memberships/${membershipId}/clear-scheduled`)
      .then((r) => r.data),

  // Family memberships: list all family member memberships
  getFamilyMemberships: () =>
    apiClient.get<FamilyMembership[]>('/payments/family-memberships').then((r) => r.data),

  // Family payment history: payments for user + all dependents
  getFamilyPaymentHistory: () =>
    apiClient.get<PaymentRecord[]>('/payments/family-payments').then((r) => r.data),

  // Pause APIs (user can view history and resume their own paused memberships)
  getPauseHistory: (membershipId: string) => apiClient
    .get<Array<{
      id: string;
      paused_by_name: string | null;
      paused_at: string;
      pause_reason: string | null;
      paused_start_date: string;
      paused_end_date: string | null;
      resumed_at: string | null;
      resumed_by_name: string | null;
      resume_reason: string | null;
      days_extended: number | null;
      is_active_pause: boolean;
    }>>(`/payments/memberships/${membershipId}/pauses`)
    .then((r) => r.data),

  resumeMembership: (membershipId: string, resumeReason?: string) => apiClient
    .post<UserMembership>(`/payments/memberships/${membershipId}/resume`, { resume_reason: resumeReason })
    .then((r) => r.data),
};
